/**
 * Formateo y parseo de fechas desde el API (Django/DRF).
 *
 * - Cadenas solo calendario "YYYY-MM-DD" o prefijo "YYYY-MM-DDT…": se interpretan como
 *   fecha civil en zona local (evita el desfase de un día de Date.parse en UTC).
 * - Valores con hora real (instante): usar formatDateTimeEsMX / new Date según el campo.
 */

const RE_DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/
const RE_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})(?:T|\s|$)/
const MEXICO_CITY_TZ = 'America/Mexico_City'

function _calendarDateFromParts(y, mo, d) {
  const dt = new Date(y, mo - 1, d)
  if (dt.getFullYear() !== y || dt.getMonth() !== mo - 1 || dt.getDate() !== d) return null
  return dt
}

function _pad2(n) {
  return String(n).padStart(2, '0')
}

function _extractYmdInTimeZone(value, timeZone = MEXICO_CITY_TZ) {
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return null
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).formatToParts(d)
  const year = parts.find((p) => p.type === 'year')?.value
  const month = parts.find((p) => p.type === 'month')?.value
  const day = parts.find((p) => p.type === 'day')?.value
  if (!year || !month || !day) return null
  return { year: parseInt(year, 10), month: parseInt(month, 10), day: parseInt(day, 10) }
}

/**
 * @param {string|Date|null|undefined} value
 * @returns {Date|null} Medianoche local del día calendario indicado, o null.
 */
export function parseApiDateOnly(value) {
  if (value === null || value === undefined || value === '') return null
  if (value instanceof Date) {
    if (isNaN(value.getTime())) return null
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const s = String(value).trim()
  let m = s.match(RE_DATE_ONLY)
  if (m) {
    return _calendarDateFromParts(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10))
  }
  m = s.match(RE_DATE_PREFIX)
  if (m) {
    return _calendarDateFromParts(parseInt(m[1], 10), parseInt(m[2], 10), parseInt(m[3], 10))
  }
  return null
}

/**
 * Inicio del día local para comparaciones (formularios, vigencias).
 * Si el valor es solo fecha o prefijo ISO fecha, usa parseApiDateOnly; si no, usa componentes locales de Date.
 * @param {string|Date|null|undefined} value
 * @returns {Date|null}
 */
export function startOfLocalDayFromApi(value) {
  const only = parseApiDateOnly(value)
  if (only) return only
  if (value === null || value === undefined || value === '') return null
  const t = value instanceof Date ? value : new Date(value)
  if (isNaN(t.getTime())) return null
  return new Date(t.getFullYear(), t.getMonth(), t.getDate())
}

const DEFAULT_DATE_DISPLAY_OPTS = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
}

/**
 * @param {string|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @param {string} [emptyDisplay]
 */
export function formatDateOnlyEsMX(value, options = {}, emptyDisplay = '') {
  if (value === null || value === undefined || value === '') return emptyDisplay
  const d = parseApiDateOnly(value)
  if (d) {
    return d.toLocaleDateString('es-MX', { ...DEFAULT_DATE_DISPLAY_OPTS, ...options })
  }
  const t = new Date(value)
  if (isNaN(t.getTime())) return emptyDisplay
  return t.toLocaleDateString('es-MX', { ...DEFAULT_DATE_DISPLAY_OPTS, ...options })
}

/**
 * Fecha y hora (instante). Respeta el comportamiento estándar de new Date (incl. Z u offset si vienen en el string).
 * @param {string|Date|null|undefined} value
 * @param {Intl.DateTimeFormatOptions} [options]
 * @param {string} [emptyDisplay]
 */
export function formatDateTimeEsMX(value, options, emptyDisplay = '') {
  if (value === null || value === undefined || value === '') return emptyDisplay
  const d = value instanceof Date ? value : new Date(value)
  if (isNaN(d.getTime())) return emptyDisplay
  if (options && Object.keys(options).length > 0) {
    return d.toLocaleString('es-MX', options)
  }
  return d.toLocaleString('es-MX')
}

/**
 * Para ordenar por fecha calendario (issue_date, due_date, etc.).
 * @param {string|Date|null|undefined} value
 * @returns {number}
 */
export function getDateOnlySortTimestamp(value) {
  const d = parseApiDateOnly(value)
  if (d) return d.getTime()
  const t = value ? new Date(value) : new Date(0)
  return isNaN(t.getTime()) ? 0 : t.getTime()
}

/**
 * Fecha civil "YYYY-MM-DD" en zona horaria CDMX.
 * @param {string|Date|null|undefined} [value]
 * @returns {string}
 */
export function toDateOnlyMexicoCity(value = new Date()) {
  const ymd = _extractYmdInTimeZone(value)
  if (!ymd) return ''
  return `${ymd.year}-${_pad2(ymd.month)}-${_pad2(ymd.day)}`
}

/**
 * Retorna "hoy" en CDMX como YYYY-MM-DD.
 */
export function getTodayDateOnlyMx() {
  return toDateOnlyMexicoCity(new Date())
}

/**
 * Suma días sobre una fecha civil y retorna YYYY-MM-DD.
 * Mantiene contrato de DateField (sin "T").
 * @param {string|Date} baseDate
 * @param {number} days
 */
export function addDaysToDateOnly(baseDate, days) {
  const base = parseApiDateOnly(baseDate)
  if (!base) return ''
  const d = new Date(base)
  d.setDate(d.getDate() + Number(days || 0))
  return `${d.getFullYear()}-${_pad2(d.getMonth() + 1)}-${_pad2(d.getDate())}`
}

/**
 * Año actual en CDMX.
 */
export function getCurrentYearMx() {
  const ymd = _extractYmdInTimeZone(new Date())
  return ymd ? ymd.year : new Date().getFullYear()
}

/**
 * Primer día del mes actual en CDMX como YYYY-MM-DD.
 */
export function getMonthStartDateOnlyMx() {
  const ymd = _extractYmdInTimeZone(new Date())
  if (!ymd) return ''
  return `${ymd.year}-${_pad2(ymd.month)}-01`
}

/**
 * Último día del mes actual en CDMX como YYYY-MM-DD.
 */
export function getMonthEndDateOnlyMx() {
  const ymd = _extractYmdInTimeZone(new Date())
  if (!ymd) return ''
  const last = new Date(ymd.year, ymd.month, 0)
  return `${last.getFullYear()}-${_pad2(last.getMonth() + 1)}-${_pad2(last.getDate())}`
}
